import {Component, Input} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {ColumnCompletion, columnCompletions} from '../value-template';

interface TemplateColumn {
  name: string;
  sample_values: string[];
}

/**
 * The Import module's `{Column}` value-template field: a plain text input
 * that completes source-column placeholders at the caret (see
 * `columnCompletions` for when it offers them). Picking a completion
 * writes the whole placeholder and puts the caret just after it, so a
 * template with several columns and literal text between them is typed in
 * one pass.
 */
@Component({
  selector: 'app-column-template-input',
  templateUrl: './column-template-input.component.html',
  styleUrls: ['./column-template-input.component.scss'],
  standalone: false,
})
export class ColumnTemplateInputComponent {
  @Input() control!: FormControl<string>;
  @Input() columns: TemplateColumn[] = [];
  @Input() label = 'TRANS__IMPORT__VALUE_TEMPLATE';

  completions: ColumnCompletion[] = [];

  /** Recomputes the completions for where the caret is now. */
  refresh(input: HTMLInputElement): void {
    this.completions = columnCompletions(
      input.value, input.selectionStart ?? input.value.length, this.columns.map(c => c.name));
  }

  /** Arrow-up/down belong to the open panel; only a key that moves the
   * caret along the text changes what is completable. */
  onKeyup(event: KeyboardEvent, input: HTMLInputElement): void {
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      this.refresh(input);
    }
  }

  /** The autocomplete has already written the chosen template into the
   * input and the control; what is left is the caret, which it leaves at
   * the end of the text. */
  onSelected(event: MatAutocompleteSelectedEvent, input: HTMLInputElement): void {
    const chosen = this.completions.find(c => c.template === event.option.value);
    if (chosen) {
      input.setSelectionRange(chosen.caret, chosen.caret);
    }
    this.refresh(input);
  }

  /** The field lost focus. With the panel open that is a click on a
   * completion or outside the panel, and `onClosed` follows; dropping the
   * completions here would pull them from under the pointer. */
  onBlur(panelOpen: boolean): void {
    if (!panelOpen) {
      this.completions = [];
    }
  }

  /** The panel closed. A field that still has focus keeps its completions:
   * the panel also closes on Escape, and an arrow key opens it again. */
  onClosed(input: HTMLInputElement): void {
    if (document.activeElement !== input) {
      this.completions = [];
    }
  }

  firstSample(column: string): string | undefined {
    return this.columns.find(c => c.name === column)?.sample_values[0];
  }
}
